import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "../components/Navbar";
import languageContext from "../context/language/languageContext";

// Reusable InputField Component
const InputField = ({ label, value, field, handleChange, type = "text" }) => (
  <div className="flex flex-col space-y-1">
    <label className="text-gray-700 font-medium">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => handleChange(e, field)}
      placeholder={`Enter ${label}`}
      className="px-4 py-3 rounded-xl border border-gray-300 bg-white shadow-inner text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-400 transition-all"
    />
  </div>
);

const Profile = () => {
  const { lang } = useContext(languageContext);
  const navigate = useNavigate();

  const [details, setDetails] = useState({
    doctor_name: '',
    doctor_number: '',
    hospital_name: '',
    university: '',
    college_name: '',
    location: '',
    uid: '',
    hostel: '',
    warden: '',
    warden_phone_number: '',
    parent_name: '',
    medical_officer: '',
    medical_officer_phone_number: '',
    patient_phone_number: ''
  });

  const [doctorList, setDoctorList] = useState([]);
  const [error, setError] = useState("");

  const handleChange = (e, field) => {
    setDetails(prev => ({ ...prev, [field]: e.target.value }));
  };

  const notadddoctor = () => {
    navigate("/ask", { state: { patientUID: details.uid } });
  };

  const submitProfile = async () => {
    for (const key in details) {
      if (!details[key].trim()) {
        setError("Please fill all fields before submitting.");
        return;
      }
    }

    try {
      const headerList = {
        Accept: "*/*",
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`
      };

      await axios.post(
        "https://www.careplus.gopillz.com/university_interaction",
        details,
        { headers: headerList }
      );

      navigate("/ask", { state: { patientUID: details.uid } });
    } catch (error) {
      console.error("Error saving the details: ", error);
      alert("Could not save your data, please try again later");
    }
  };

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const headers = {
          Accept: "*/*",
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`
        };

        const res = await axios.post(
          "https://www.careplus.gopillz.com/get_unviersity_interaction",
          {},
          { headers }
        );

        const data = res.data.data;
        setDetails(prev => ({ ...prev, ...data }));
        if (data.doctor) setDoctorList(data.doctor);
      } catch (err) {
        console.error("Error fetching doctor/university details", err);
      }
    };

    fetchDetails();
  }, []);

  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar />
      <div className="max-w-5xl mx-auto p-8 bg-white/80 backdrop-blur-md shadow-2xl rounded-3xl mt-12">
        {error && <p className="text-red-500 font-medium text-center mb-6">{error}</p>}

        <div className="grid grid-cols-1 gap-6">
          {/* LINE 1 - Doctor Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <InputField label="Doctor Name" value={details.doctor_name} field="doctor_name" handleChange={handleChange} />
            <InputField label="Doctor Phone No." value={details.doctor_number} field="doctor_number" handleChange={handleChange} type="tel" />
            <InputField label="Doctor Hospital" value={details.hospital_name} field="hospital_name" handleChange={handleChange} />
          </div>

          {/* LINE 2 - Hostel, College/University, UID */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <InputField label="Your Hostel" value={details.hostel} field="hostel" handleChange={handleChange} />
            <InputField label="College / University Name" value={details.university || details.college_name} field={details.university ? "university" : "college_name"} handleChange={handleChange} />
            <InputField label="ID No. (UID)" value={details.uid} field="uid" handleChange={handleChange} />
          </div>

          {/* LINE 3 - Parent Contact */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <InputField label="Parent Name" value={details.parent_name} field="parent_name" handleChange={handleChange} />
            <InputField label="Parent Phone No." value={details.patient_phone_number} field="patient_phone_number" handleChange={handleChange} type="tel" />
            <div /> {/* Empty cell to keep layout uniform */}
          </div>

          {/* LINE 4 - Warden Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <InputField label="Warden Name" value={details.warden} field="warden" handleChange={handleChange} />
            <InputField label="Hostel Name" value={details.hostel} field="hostel" handleChange={handleChange} />
            <InputField label="Warden Phone No." value={details.warden_phone_number} field="warden_phone_number" handleChange={handleChange} type="tel" />
          </div>

          {/* LINE 5 - Medical Officer Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <InputField label="Medical Officer Name" value={details.medical_officer} field="medical_officer" handleChange={handleChange} />
            <InputField label="Medical Officer Phone No." value={details.medical_officer_phone_number} field="medical_officer_phone_number" handleChange={handleChange} type="tel" />
            <div /> {/* Empty cell */}
          </div>
        </div>

        {/* Doctor list */}
        {doctorList.length > 0 && (
          <div className="mt-10">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Associated Doctors</h3>
            <div className="space-y-4">
              {doctorList.map((doc, index) => (
                <div key={index} className="bg-white shadow p-4 rounded-xl border border-gray-200">
                  <p><strong>Name:</strong> {doc.doctor_name}</p>
                  <p><strong>Phone:</strong> {doc.phone_number}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Buttons */}
        <div className="flex flex-col sm:flex-row justify-center items-center gap-6 mt-12">
          <button
            type="button"
            className="bg-gradient-to-r from-teal-500 to-blue-500 hover:from-teal-600 hover:to-blue-600 text-white font-semibold py-3 px-8 rounded-full shadow-md hover:scale-105 transition-transform"
            onClick={submitProfile}
          >
            Proceed
          </button>

        </div>
      </div>
    </div>
  );
};

export default Profile;
